<template>
	<section id="explore_area" class="section_padding">
		<div class="container">
			<!-- Section Heading -->
			<div class="row">
				<div class="col-lg-3 col-md-3 col-sm-12 col-12">
					<h2>{{ $t("filters") }}</h2>
					<br />
				</div>
				<div class="col-lg-9 col-md-9 col-sm-12 col-12">
					<h2>{{ $t("allies_found") }}</h2>
					<br />
				</div>
			</div>
			<div class="row">
				<div class="col-lg-3">
					<div class="left_side_search_area">
						<div class="left_side_search_boxed">
							<div class="left_side_search_heading">
								<h5>{{ $t("filter_by_categories") }}</h5>
							</div>
							<div class="filter_review">
								<div class="form-group">
									<v-select class="form-control bg_input" label="text"
										:reduce="(category) => category" v-model="selectedCategory"
										:options="categories" :placeholder="$t('select_category')">
									</v-select>
								</div>
							</div>
							<br />
							<button class="apply" type="button" :disabled="isLoading" @click="applyFilters()">
								<span v-if="!isLoading">
									{{ $t("apply") }}
									<i class="px-2 fas fa-angle-right text-coral"></i>
								</span>
								<div v-else class="loader"></div>
							</button>
						</div>
						<div class="left_side_search_boxed">
							<div class="left_side_search_heading">
								<h5>{{ $t("filter_by_subcategories") }}</h5>
							</div>
							<div class="filter_review">
								<div class="form-group">
									<v-select class="form-control bg_input" label="text"
										:reduce="(subcategory) => subcategory.id" v-model="selectedSubCategory"
										:options="subcategories" :placeholder="$t('select_subcategory')">
									</v-select>
								</div>
							</div>
							<br />
							<button class="apply" type="button" :disabled="isLoading" @click="applyFilters()">
								<span v-if="!isLoading">
									{{ $t("apply") }}
									<i class="px-2 fas fa-angle-right text-coral"></i>
								</span>
								<div v-else class="loader"></div>
							</button>
							<p v-if="selectedCategory && !selectedSubCategory" class="text-coral">
								{{ $t("subcategory_required") }}
							</p>
						</div>
						<div class="left_side_search_boxed">
							<div class="left_side_search_heading">
								<h5>{{ $t("filter_by_discount_percentage") }}</h5>
							</div>
							<div class="form-group">
								<span class="multi-range">
									<input type="range" :min="minDiscountRange" :max="maxDiscountRange"
										:value="minDiscount" @input="updateMinDiscount" id="lower" />
									<input class="position_ball" type="range" :min="minDiscountRange"
										:max="maxDiscountRange" :value="maxDiscount" @input="updateMaxDiscount"
										id="upper" />
								</span>
								<div class="range-values">
									<span>{{ formatDiscount(minDiscount) }}</span>
									<span>{{ formatDiscount(maxDiscount) }}</span>
								</div>
							</div>
							<br />
							<button class="apply" type="button" :disabled="isLoading" @click="applyFilters()">
								<span v-if="!isLoading">
									{{ $t("apply") }}
									<i class="px-2 fas fa-angle-right text-coral"></i>
								</span>
								<div v-else class="loader"></div>
							</button>
						</div>
						<div class="left_side_search_boxed">
							<div class="left_side_search_heading">
								<h5>{{ $t("filter_by_cities") }}</h5>
							</div>
							<div class="filter_review">
								<div class="form-group">
									<v-select class="form-control bg_input" label="text" multiple
										:reduce="(city) => city.id" v-model="selectedCities" :options="cities"
										:placeholder="$t('select_cities')">
									</v-select>
								</div>
							</div>
							<br />
							<button class="apply" type="button" :disabled="isLoading" @click="applyFilters()">
								<span v-if="!isLoading">
									{{ $t("apply") }}
									<i class="px-2 fas fa-angle-right text-coral"></i>
								</span>
								<div v-else class="loader"></div>
							</button>
						</div>
						<div class="left_side_search_boxed">
							<div class="left_side_search_heading">
								<h5>{{ $t("filter_by_departments") }}</h5>
							</div>
							<div class="filter_review">
								<div class="form-group">
									<v-select class="form-control bg_input" label="text" multiple
										:reduce="(state) => state.id" v-model="selectedDepartments"
										:options="departments" :placeholder="$t('select_departments')">
									</v-select>
								</div>
							</div>
							<br />
							<button class="apply" type="button" :disabled="isLoading" @click="applyFilters()">
								<span v-if="!isLoading">
									{{ $t("apply") }}
									<i class="px-2 fas fa-angle-right text-coral"></i>
								</span>
								<div v-else class="loader"></div>
							</button>
						</div>
					</div>
				</div>
				<div class="col-lg-9">
					<div class="row" ref="gridContainer">
						<div v-for="(item, index) in filteredData" :key="index" class="col-lg-4 col-md-6 col-sm-12">
							<div class="theme_common_box_two img_hover" @click="handleCardClick(item, index)"
								:data-ally-id="String(item.id)" :data-card-index="index">
								<div class="theme_two_box_img">
									<div v-if="hasDiscount(item.discount_percentage)" class="discount-badge">
										{{ formatDiscount(item.discount_percentage) }}
									</div>
									<img :src="item.images?.[0]?.path || '/default-image.jpg'"
										:alt="item.category?.name" @click.stop="handleImageClick(item, index)" />
								</div>
								<div class="theme_two_box_content">
									<p class="card-line card-line--name" :title="item.name">
										{{ item.name }}
									</p>
									<p v-if="getCategoryLabel(item)" class="card-line card-line--meta"
										:title="getCategoryLabel(item)">
										{{ getCategoryLabel(item) }}
									</p>
								</div>
							</div>
						</div>
						<div class="col-lg-9">
							<div class="pagination_area">
								<ul class="pagination">
									<li class="page-item" :class="{ disabled: meta.current_page === 1 }">
										<a class="page-link" @click="prev" :disabled="meta.current_page === 1"
											aria-label="Previous">
											<span aria-hidden="true">&laquo;</span>
											<span class="sr-only">Previous</span>
										</a>
									</li>

									<li class="page-item" v-for="page in meta.last_page" :key="page"
										:class="{ active: meta.current_page === page }">
										<a class="page-link" @click="goToPage(page)">{{ page }}</a>
									</li>

									<li class="page-item" :class="{ disabled: meta.current_page === meta.last_page }">
										<a class="page-link" @click="next"
											:disabled="meta.current_page === meta.last_page" aria-label="Next">
											<span aria-hidden="true">&raquo;</span>
											<span class="sr-only">Next</span>
										</a>
									</li>
								</ul>
							</div>
						</div>

						<div v-if="showModal" class="modal-overlay" @click.self="closeModal">
							<div class="modal-content modal-sm">
								<div class="modal-header">
									<button @click="closeModal" class="close-btn">&times;</button>
								</div>
								<div class="modal-body">
									<div v-if="selectedItem">
                                        <div class="swiper-container">
                                            <div class="swiper-wrapper">
                                                <div v-for="(image, index) in selectedItem.images || []" :key="index"
                                                    class="swiper-slide">
                                                    <img :src="image.path" :alt="selectedItem.category.name"
                                                        class="swiper-modal-image" />
                                                </div>
                                            </div>
                                            <div class="swiper-button-next"></div>
                                            <div class="swiper-button-prev"></div>
                                        </div>
										<div class="modal-info">
											<div class="title-section">
												<h2>{{ selectedItem.name }}</h2>
												<div class="contact-icons">
													<a :href="`https://wa.me/${selectedItem.whatsapp}`" target="_blank"
														class="contact-icon whatsapp"
														@click.stop.prevent="handleWhatsAppClick(selectedItem, selectedItem.whatsapp, 'primary')">
														<i class="fab fa-whatsapp"></i>
													</a>
													<a v-if="selectedItem.whatsapp_2"
														:href="`https://wa.me/${selectedItem.whatsapp_2}`"
														target="_blank" class="contact-icon whatsapp"
														@click.stop.prevent="handleWhatsAppClick(selectedItem, selectedItem.whatsapp_2, 'secondary')">
														<i class="fab fa-whatsapp">
															<small
																style="position: relative; font-size: 8px;margin-left: 2px;">2</small>

														</i>
													</a>
													<a v-if="selectedItem.contact_link"
														:href="selectedItem.contact_link" target="_blank"
														class="contact-icon website"
														:title="$t('more_info')"
														@click.stop.prevent="handleInfoLinkClick(selectedItem)">
														<i class="fas fa-external-link-alt"></i>
														
													</a>
													<button type="button" class="contact-icon share"
														:title="$t('share')"
														@click.stop.prevent="handleShareClick(selectedItem, 'modal')">
														<i class="fas fa-share-alt"></i>
													</button>
												</div>
												<div v-if="shareFeedback" class="share-feedback">
													{{ shareFeedback }}
												</div>
											</div>
											<p class="category">
												{{ selectedItem.category?.name || "" }}
												<span
													v-if="selectedItem.category?.name && selectedItem.sub_category?.name">-</span>
												{{ selectedItem.sub_category?.name || "" }}
											</p>
											<p v-if="hasDiscount(selectedItem.discount_percentage)"
												class="discount-line">
												<strong>{{ $t("discount_percentage") }}:</strong>
												<span class="discount-pill">
													{{ formatDiscount(selectedItem.discount_percentage) }}
												</span>
											</p>
											<p v-if="allyDiscountObservation(selectedItem)" class="discount-observation">
												<strong>{{ $t("ally_modal.discount_observation") }}:</strong>
												{{ allyDiscountObservation(selectedItem) }}
											</p>
											<div class="benefit-actions">
												<button type="button" class="benefit-button"
													:disabled="isBenefitDisabled(selectedItem)"
													:title="benefitTooltip(selectedItem)"
													@click.stop.prevent="handleBenefitClick(selectedItem)">
													{{ benefitLabel(selectedItem) }}
												</button>
											</div>
											<div v-if="benefitInfo" class="benefit-feedback">
												{{ benefitInfo }}
											</div>
											<transition name="fade">
												<BenefitForm v-if="showBenefitForm" :ally="selectedItem"
													:prefill-user="prefillUser" @submitted="handleBenefitSubmitted"
													@close="handleBenefitClose" @auth-required="handleAuthRequired" />
											</transition>
											<p class="location mt-3">
												<strong>{{ $t("location") }}:</strong>
												{{ selectedItem.state?.name }} -
												{{ selectedItem.city?.name }}
												<a :href="`https://www.google.com/maps?q=${selectedItem.latitude},${selectedItem.longitude}`"
													target="_blank" class="maps-link"
													@click.stop.prevent="handleMapClick(selectedItem)">
													<i class="fas fa-map-marker-alt"></i>
												</a>
											</p>
											<p v-if="selectedItem.phone" class="phone">
												<strong>{{ $t("phone") }}:</strong> {{ selectedItem.phone }}
											</p>
											<p v-if="agreementValidityText(selectedItem)" class="agreement-validity">
												<strong>{{ $t("ally_modal.agreement_validity") }}:</strong>
												{{ agreementValidityText(selectedItem) }}
											</p>
											<div v-if="allyHasDescription(selectedItem)" class="description mt-3">
												<strong class="d-block">{{ $t("description") }}:</strong>
												<div class="description-content"
													v-html="allyDescriptionHtml(selectedItem)"></div>
											</div>
										</div>
										<Comments :key="selectedItem.id" :model-id="selectedItem.id"
											:model-type="'ally'" />

									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>
<script>
import axios from "axios";
import {
	API_BASE_URL,
	ALLIES_INTEREST_API,
	CITY_API,
	STATE_API,
	PLACE_CATEGORIES_API,
	PLACE_SUBCATEGORIES_API,
} from "@/api-config";
import Swiper from "swiper";
import "swiper/swiper-bundle.css";
import Comments from "@/components/comment/comment.vue";
import BenefitForm from "@/components/alliances/BenefitForm.vue";
import { isAllyInForce, getAllyValidity } from "@/composables/useAllies";
import { trackEvent } from "@/composables/useMetrics";

const SUBJECT_TYPE = "ally";
const PAGE_CONTEXT = "ally_list";
const MIN_DISCOUNT_GAP = 1;


export default {
	name: "AlliesResult",
	components: { Comments, BenefitForm },
	data() {
		return {
			cities: [],
			departments: [],
			selectedCities: null,
			selectedDepartments: null,
			categories: [],
			selectedCategory: null,
			subcategories: [],
			selectedSubCategory: null,
			minDiscount: 0,
			maxDiscount: 0,
			minDiscountRange: 0,
			maxDiscountRange: 0,
			apiData: [],
			filteredData: [],
			selectedItem: null,
			showModal: false,
			showBenefitForm: false,
			hoverText: "",
			shareFeedback: "",
			shareFeedbackTimeout: null,
			prefillUser: null,
			isAuth: !!localStorage.getItem("token"),
			benefitInfo: "",
			benefitInfoTimeout: null,
			pendingSharedId: null,
			lastSharedItemId: null,
			meta: {
				current_page: 1,
				last_page: 1,
				min_discount: 0,
				max_discount: 0,
			},
			links: {},
			isLoading: false,
			city_id: 0,
			state_id: 0,
			metricsLocks: {
				image: Object.create(null),
				info: Object.create(null),
				whatsapp: Object.create(null),
				map: Object.create(null),
				view: Object.create(null),
				share: Object.create(null),
			},
			intersectionObserver: null,
		};
	},

	async mounted() {
		this.loadPrefillUser();
		this.city_id = this.$route.query.city_id;
		this.state_id = this.$route.query.state_id;
		this.pendingSharedId = this.parsePositiveInt(this.$route.query.ally_id);
		const initialPage = this.parsePositiveInt(this.$route.query.page) ?? 1;
		await this.fetchData(initialPage);
		this.getCities();
		this.getStates();
		this.getCategories();

		if (this.city_id) {
			this.selectedCities = [Number(this.city_id)];
			this.applyFilters();
		}
		if (this.state_id) {
			this.selectedDepartments = [Number(this.state_id)];
			this.applyFilters();
		}
	},

	watch: {
		"$route.query.city_id"(newCityId) {
			if (newCityId) {
				this.selectedCities = [Number(newCityId)];
				this.applyFilters();
			} else {
				this.selectedCities = null;
				this.applyFilters();
			}
		},
		"$route.query.state_id"(newStateId) {
			if (newStateId) {
				this.selectedDepartments = [Number(newStateId)];
				this.applyFilters();
			} else {
				this.selectedDepartments = null;
				this.applyFilters();
			}
		},
		"$route.query.ally_id"(newId) {
			this.pendingSharedId = this.parsePositiveInt(newId);
			if (!this.pendingSharedId) {
				this.lastSharedItemId = null;
				return;
			}
			this.tryOpenSharedItem();
		},
		"$route.query.page"(newPage) {
			const targetPage = this.parsePositiveInt(newPage) ?? 1;
			if (targetPage !== this.meta.current_page) {
				this.fetchData(targetPage);
			}
		},
		selectedCategory(newCategory) {
			if (newCategory) {
				this.getSubcategories(newCategory.id);
				this.selectedSubCategory = null;
				this.subcategories = [];
				this.selectedSubCategory = null;
			}
		},
		filteredData() {
			this.setupVisibilityObserver();
			this.tryOpenSharedItem();
		},
		showModal(isOpen) {
			if (!isOpen) {
				this.showBenefitForm = false;
				this.setBenefitInfo("");
			}
		},
	},

	beforeUnmount() {
		this.teardownVisibilityObserver();
		this.clearShareFeedback();
		this.showBenefitForm = false;
		if (this.benefitInfoTimeout) {
			clearTimeout(this.benefitInfoTimeout);
			this.benefitInfoTimeout = null;
		}
	},

	methods: {
		loadPrefillUser() {
			try {
				const stored = localStorage.getItem("user");
				if (!stored) {
					this.prefillUser = null;
					this.isAuth = !!localStorage.getItem("token");
					return;
				}
				this.prefillUser = JSON.parse(stored);
				this.isAuth = !!localStorage.getItem("token");
			} catch (error) {
				console.warn("Failed to load stored user profile", error);
				this.prefillUser = null;
				this.isAuth = !!localStorage.getItem("token");
			}
		},
		async ensureAllyDetail() {
			// Full ally information is already present in this listing context.
		},
		handleAuthRequired() {
			this.isAuth = !!localStorage.getItem("token");
			this.loadPrefillUser();
			this.showBenefitForm = false;
			this.setBenefitInfo("");
		},
		setBenefitInfo(message) {
			if (this.benefitInfoTimeout) {
				clearTimeout(this.benefitInfoTimeout);
				this.benefitInfoTimeout = null;
			}
			this.benefitInfo = message || "";
			if (message && typeof window !== "undefined") {
				this.benefitInfoTimeout = window.setTimeout(() => {
					this.benefitInfo = "";
					this.benefitInfoTimeout = null;
				}, 4000);
			}
		},
		agreementValidityText(ally) {
			if (!ally) {
				return "";
			}
			const { from, to } = getAllyValidity(ally);
			if (!from && !to) {
				const fallback = ally.validity_of_agreement || ally.agreement_valid_to;
				if (!fallback) {
					return "";
				}
				const date = new Date(fallback);
				if (Number.isNaN(date.getTime())) {
					return fallback;
				}
				return new Intl.DateTimeFormat(undefined, {
					day: "2-digit",
					month: "short",
					year: "numeric",
				}).format(date);
			}
			const formatter = new Intl.DateTimeFormat(undefined, {
				day: "2-digit",
				month: "short",
				year: "numeric",
			});
			const formattedFrom = from ? formatter.format(from) : null;
			const formattedTo = to ? formatter.format(to) : null;
			if (formattedFrom && formattedTo) {
				return this.$t("ally_modal.validity_range", { from: formattedFrom, to: formattedTo });
			}
			if (formattedFrom) {
				return this.$t("ally_modal.validity_from", { from: formattedFrom });
			}
			if (formattedTo) {
				return this.$t("ally_modal.validity_to", { to: formattedTo });
			}
			return "";
		},
		benefitLabel(ally) {
			if (!ally) {
				return this.$t("ally_modal.redeem");
			}
			if (!isAllyInForce(ally)) {
				return this.$t("ally_modal.redeem_disabled");
			}
			if (!this.isAuth) {
				return this.$t("ally_modal.redeem_login");
			}
			return this.$t("ally_modal.redeem");
		},
		isBenefitDisabled() {
			return !this.isAuth;
		},
		benefitTooltip(ally) {
			if (!ally) {
				return "";
			}
			if (!isAllyInForce(ally)) {
				const { from, to } = getAllyValidity(ally);
				const formatter = new Intl.DateTimeFormat(undefined, {
					day: "2-digit",
					month: "short",
					year: "numeric",
				});
				const formattedFrom = from ? formatter.format(from) : null;
				const formattedTo = to ? formatter.format(to) : null;
				if (formattedFrom && formattedTo) {
					return this.$t("ally_modal.validity_range", { from: formattedFrom, to: formattedTo });
				}
				if (formattedFrom) {
					return this.$t("ally_modal.validity_from", { from: formattedFrom });
				}
				if (formattedTo) {
					return this.$t("ally_modal.validity_to", { to: formattedTo });
				}
				return this.$t("ally_modal.agreement_expired");
			}
			if (!this.isAuth) {
				return this.$t("ally_modal.disabled_login");
			}
			return "";
		},
		async handleBenefitClick(ally) {
			if (!ally) {
				return;
			}
			if (!this.isAuth) {
				this.redirectToLogin();
				return;
			}
			if (this.showBenefitForm) {
				this.showBenefitForm = false;
				this.setBenefitInfo("");
				return;
			}
			if (!isAllyInForce(ally)) {
				this.setBenefitInfo(this.benefitTooltip(ally));
				return;
			}
			await this.ensureAllyDetail(ally);
			this.showBenefitForm = true;
			this.setBenefitInfo("");
		},
		handleBenefitClose() {
			this.showBenefitForm = false;
			this.setBenefitInfo("");
		},
		handleBenefitSubmitted() {
			this.showBenefitForm = true;
			this.loadPrefillUser();
			this.setBenefitInfo("");
		},
		redirectToLogin() {
			const redirect = this.$route?.fullPath || this.$router?.currentRoute?.value?.fullPath || "/";
			this.$router.push({
				name: "login",
				query: { redirect },
			});
		},
		getCategoryLabel(item) {
			if (!item) {
				return "";
			}
			const parts = [];
			if (item.category?.name) {
				parts.push(item.category.name);
			}
			if (item.sub_category?.name) {
				parts.push(item.sub_category.name);
			}
			return parts.join(" - ");
		},
		handleCardClick(item, position) {
			this.openModal(item);
			this.emitInfoClick(item, position, "card");
		},
		handleImageClick(item, position) {
			this.openModal(item);
			this.emitImageClick(item, position);
		},
		handleWhatsAppClick(item, phone, channel) {
			if (!item?.id || !phone) {
				return;
			}
			this.openExternal(`https://wa.me/${phone}`);
			const action = channel === "secondary" ? "whatsapp_secondary" : "whatsapp_primary";
			this.emitAction(item, action, this.metricsLocks.whatsapp, `${item.id}-${channel}`, {
				location: "modal",
			});
		},
		handleInfoLinkClick(item) {
			if (!item?.id || !item?.contact_link) {
				return;
			}
			this.openExternal(item.contact_link);
			this.emitInfoClick(item, null, "external_link", { location: "modal" });
		},
		handleMapClick(item) {
			if (!item?.id || !item?.latitude || !item?.longitude) {
				return;
			}
			const url = `https://www.google.com/maps?q=${item.latitude},${item.longitude}`;
			this.openExternal(url);
			this.emitAction(item, "map_click", this.metricsLocks.map, String(item.id), {
				location: "modal",
			});
		},
		async handleShareClick(item, source = "modal") {
			if (!item?.id || typeof window === "undefined") {
				return;
			}
			const shareUrl = this.buildShareUrl(item);
			const shareMessage = this.$t("share_message_ally", { name: item.name ?? "" });
			const combinedText = shareMessage && shareUrl ? `${shareMessage}\n${shareUrl}` : shareMessage || shareUrl;
			const sharePayload = {
				title: `${item.name ?? "SELAH"} · SELAH`,
				text: shareMessage,
				url: shareUrl,
			};
			const shareKey = `${item.id}-share-${source}`;
			let method = "navigator";

			if (navigator.share) {
				try {
					await navigator.share(sharePayload);
					this.showShareFeedback(this.$t("share_ready"));
					this.emitAction(item, "click_share", this.metricsLocks.share, shareKey, {
						location: source,
						method,
					});
					return;
				} catch (error) {
					if (error?.name === "AbortError") {
						return;
					}
				}
			}

			method = "clipboard";
			let copied = false;

			if (navigator.clipboard && window.isSecureContext && combinedText) {
				try {
					await navigator.clipboard.writeText(combinedText);
					copied = true;
				} catch (error) {
					copied = false;
				}
			}

			if (!copied && combinedText) {
				copied = this.copyTextFallback(combinedText);
				method = copied ? "fallback" : "manual";
			}

			if (copied) {
				this.showShareFeedback(this.$t("share_link_copied"));
			} else {
				this.showShareFeedback(this.$t("share_copy_prompt"));
			}

			this.emitAction(item, "click_share", this.metricsLocks.share, shareKey, {
				location: source,
				method,
			});
		},
		emitImageClick(item, position) {
			if (!item?.id) {
				return;
			}
			const key = `${item.id}-${position ?? 0}`;
			this.emitAction(item, "card_info", this.metricsLocks.image, key, {
				location: "card",
				position: position ?? 0,
			});
		},
		emitInfoClick(item, position, trigger, extraMeta = {}) {
			if (!item?.id) {
				return;
			}
			const key = `${item.id}-${trigger ?? "info"}-${position ?? "na"}`;
			const action = trigger === "external_link" ? "external_info" : "card_info";
			this.emitAction(item, action, this.metricsLocks.info, key, {
				location: extraMeta.location ?? "card",
				trigger,
				position: position ?? undefined,
				...extraMeta,
			});
		},
		emitAction(item, action, store, key, meta) {
			if (store[key]) {
				return;
			}
			store[key] = true;
			const payloadMeta = {
				page: PAGE_CONTEXT,
				...meta,
			};
			trackEvent(SUBJECT_TYPE, item.id, action, payloadMeta).finally(() => {
				store[key] = false;
			});
		},
		emitListView(item, position) {
			const key = `${item.id}-list-view-${position ?? "na"}`;
			this.emitAction(item, "list_view", this.metricsLocks.view, key, {
				location: "list",
				position: position ?? undefined,
			});
		},
		setupVisibilityObserver() {
			if (!Array.isArray(this.filteredData) || this.filteredData.length === 0) {
				this.teardownVisibilityObserver();
				return;
			}
			this.$nextTick(() => {
				this.teardownVisibilityObserver();
				const container = this.$refs.gridContainer;
				if (!container) {
					return;
				}
				const observer = new IntersectionObserver(this.handleCardIntersect, {
					root: null,
					threshold: 0.6,
				});
				this.intersectionObserver = observer;
				container.querySelectorAll(".theme_common_box_two").forEach((node) => observer.observe(node));
			});
		},
		teardownVisibilityObserver() {
			if (this.intersectionObserver) {
				this.intersectionObserver.disconnect();
				this.intersectionObserver = null;
			}
		},
		handleCardIntersect(entries) {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) {
					return;
				}
				const { allyId, cardIndex } = entry.target?.dataset || {};
				if (!allyId) {
					return;
				}
				const ally = this.filteredData.find((allyItem) => String(allyItem.id) === allyId);
				if (!ally) {
					return;
				}
				const position = Number(cardIndex);
				this.emitListView(ally, Number.isNaN(position) ? undefined : position);
			});
		},
		openExternal(url) {
			if (typeof window !== "undefined") {
				window.open(url, "_blank", "noopener");
			}
		},
		allyDescriptionRaw(ally) {
			if (!ally) {
				return "";
			}
			const candidates = [
				ally.description,
				ally.descriptions,
				ally.detail,
				ally.details,
				ally.benefit_description,
				ally.benefitDescription,
			];
			return candidates.find((value) => typeof value === "string" && value.trim())?.trim() || "";
		},
		allyDescriptionHtml(ally) {
			return this.allyDescriptionRaw(ally);
		},
		allyHasDescription(ally) {
			const html = this.allyDescriptionRaw(ally);
			if (!html) {
				return false;
			}
			const plain = html
				.replace(/<[^>]*>/g, " ")
				.replace(/&nbsp;/gi, " ")
				.replace(/\s+/g, " ")
				.trim();
			return plain.length > 0;
		},
		allyDiscountObservation(ally) {
			if (!ally) {
				return "";
			}
			const candidates = [
				ally.discount_observation,
				ally.discountObservation,
				ally.discount_observations,
				ally.benefit_observation,
				ally.benefitObservation,
				ally.observation,
				ally.observations,
			];
			return candidates.find((value) => typeof value === "string" && value.trim())?.trim() || "";
		},
		buildShareUrl(item) {
			if (!item) {
				return "";
			}
			if (typeof window === "undefined" || !this.$router) {
				return "";
			}
			const origin = window.location?.origin ?? "";
			const query = { ally_id: item.id };
			const currentPage = Number(this.meta?.current_page);
			if (Number.isFinite(currentPage) && currentPage > 0) {
				query.page = currentPage;
			}
			const resolved = this.$router.resolve({
				name: "ally-interest",
				query,
			});
			return `${origin}${resolved.href}`;
		},
		showShareFeedback(message) {
			this.shareFeedback = message;
			if (this.shareFeedbackTimeout) {
				clearTimeout(this.shareFeedbackTimeout);
			}
			if (message) {
				this.shareFeedbackTimeout = window.setTimeout(() => {
					this.shareFeedback = "";
					this.shareFeedbackTimeout = null;
				}, 3500);
			}
		},
		clearShareFeedback() {
			if (this.shareFeedbackTimeout) {
				clearTimeout(this.shareFeedbackTimeout);
				this.shareFeedbackTimeout = null;
			}
			this.shareFeedback = "";
		},
		copyTextFallback(text) {
			if (typeof window === "undefined" || typeof document === "undefined") {
				return false;
			}
			const textarea = document.createElement("textarea");
			textarea.value = text;
			textarea.setAttribute("readonly", "");
			textarea.style.position = "absolute";
			textarea.style.left = "-9999px";
			document.body.appendChild(textarea);
			const selection = document.getSelection();
			const selectedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
			textarea.select();
			let copied = false;
			try {
				copied = document.execCommand("copy");
			} catch (error) {
				copied = false;
			}
			document.body.removeChild(textarea);
			if (selectedRange && selection) {
				selection.removeAllRanges();
				selection.addRange(selectedRange);
			}
			return copied;
		},
		async fetchData(page = 1) {
			this.isLoading = true;
			try {
				const response = await axios.get(
					`${API_BASE_URL}${ALLIES_INTEREST_API}?page=${page}`
				);
				const payload = response?.data ?? {};
				const meta = payload.meta ?? {};

				this.apiData = Array.isArray(payload.data) ? payload.data : [];
				this.filteredData = this.apiData;
				this.links = payload.links ?? {};
				this.meta = {
					...this.meta,
					min_discount: meta.min_discount ?? 0,
					max_discount: meta.max_discount ?? 0,
				};

				this.minDiscountRange = this.meta.min_discount;
				this.maxDiscountRange = this.meta.max_discount;

				this.minDiscount = this.minDiscountRange;
				this.maxDiscount = this.maxDiscountRange;
				this.setupVisibilityObserver();
				const currentPage = Number(this.meta?.current_page ?? page);
				const routePage = this.parsePositiveInt(this.$route.query.page) ?? 1;
				if (Number.isFinite(currentPage) && currentPage > 0 && currentPage !== routePage) {
					this.updateRouteQuery({ page: currentPage === 1 ? null : currentPage });
				}
				if (!this.pendingSharedId && this.parsePositiveInt(this.$route.query.ally_id)) {
					this.pendingSharedId = this.parsePositiveInt(this.$route.query.ally_id);
				}
				this.tryOpenSharedItem();
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				this.isLoading = false;
			}
		},
		getCities() {
			axios
				.get(`${API_BASE_URL}${CITY_API}&has=ally`)
				.then((response) => {
					this.cities = response.data.results;
				})
				.catch((error) => {
					console.log(error);
				});
		},

		getStates() {
			axios
				.get(`${API_BASE_URL}${STATE_API}&has=ally`)
				.then((response) => {
					this.departments = response.data.results;
				})
				.catch((error) => {
					console.log(error);
				});
		},

		getCategories() {
			axios
				.get(`${API_BASE_URL}${PLACE_CATEGORIES_API}?has=ally`)
				.then((response) => {
					this.categories = response.data.results;
				})
				.catch((error) => {
					console.log(error);
				});
		},
		getSubcategories(categoryId) {
			axios
				.get(
					`${API_BASE_URL}${PLACE_SUBCATEGORIES_API}?category_id=${categoryId}&has=ally`
				)
				.then((response) => {
					this.subcategories = response.data.results;
				})
				.catch((error) => {
					console.log(error);
				});
		},
		applyFilters() {
			let filtered = this.apiData;

			if (this.selectedSubCategory) {
				filtered = filtered.filter(
					(item) => item.sub_category?.id === this.selectedSubCategory
				);
			}

			if (
				this.minDiscount != null &&
				this.maxDiscount != null &&
				!Number.isNaN(this.minDiscount) &&
				!Number.isNaN(this.maxDiscount)
			) {
				filtered = filtered.filter((item) => {
					const discount = Number(item?.discount_percentage ?? 0);
					if (!Number.isFinite(discount)) {
						return false;
					}
					return discount >= this.minDiscount && discount <= this.maxDiscount;
				});
			}

			if (this.selectedCities && this.selectedCities.length > 0) {
				filtered = filtered.filter((item) =>
					this.selectedCities.includes(item.city?.id)
				);
			}

			if (this.selectedDepartments && this.selectedDepartments.length > 0) {
				filtered = filtered.filter((item) =>
					this.selectedDepartments.includes(item.state?.id)
				);
			}

			this.filteredData = filtered;
			this.setupVisibilityObserver();
		},

		validateInput() {
			if (this.minDiscount < this.minDiscountRange || Number.isNaN(this.minDiscount)) {
				this.minDiscount = this.minDiscountRange;
			}
			if (this.maxDiscount > this.maxDiscountRange || Number.isNaN(this.maxDiscount)) {
				this.maxDiscount = this.maxDiscountRange;
			}
		},

		updateMinDiscount(event) {
			const value = Number(event.target.value);
			if (!Number.isFinite(value)) {
				return;
			}
			const upperBound = this.maxDiscount - MIN_DISCOUNT_GAP;
			const lowerBound = this.minDiscountRange;
			this.minDiscount = Math.max(lowerBound, Math.min(value, upperBound));
		},

		updateMaxDiscount(event) {
			const value = Number(event.target.value);
			if (!Number.isFinite(value)) {
				return;
			}
			const lowerBound = this.minDiscount + MIN_DISCOUNT_GAP;
			const upperBound = this.maxDiscountRange;
			this.maxDiscount = Math.min(upperBound, Math.max(value, lowerBound));
		},
		hasDiscount(value) {
			const parsed = Number(value);
			return Number.isFinite(parsed) && parsed > 0;
		},
		formatDiscount(value) {
			const parsed = Number(value);
			if (!Number.isFinite(parsed)) {
				return "0%";
			}
			const formatted = new Intl.NumberFormat("es-ES", {
				style: "decimal",
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
				useGrouping: true,
			}).format(parsed);
			return `${formatted}%`;
		},
		tryOpenSharedItem() {
			const sharedId = this.pendingSharedId;
			if (!sharedId || !Array.isArray(this.filteredData) || this.filteredData.length === 0) {
				return;
			}
			const match = this.filteredData.find((item) => Number(item.id) === sharedId);
			if (!match) {
				return;
			}
			if (this.showModal && Number(this.selectedItem?.id) === sharedId) {
				return;
			}
			this.lastSharedItemId = sharedId;
			this.openModal(match, { keepRoute: true });
		},
		parsePositiveInt(value) {
			const parsed = Number(value);
			if (!Number.isFinite(parsed) || parsed <= 0) {
				return null;
			}
			return Math.trunc(parsed);
		},
		updateRouteQuery(patch = {}) {
			if (!this.$router) {
				return;
			}
			const nextQuery = { ...this.$route.query };
			Object.entries(patch).forEach(([key, val]) => {
				if (val === null || val === undefined || val === "") {
					delete nextQuery[key];
				} else {
					nextQuery[key] = String(val);
				}
			});
			this.$router.replace({ query: nextQuery }).catch(() => { });
		},
		changePage(page) {
			const targetPage = this.parsePositiveInt(page);
			if (!targetPage) {
				return;
			}
			const currentQueryPage = this.parsePositiveInt(this.$route.query.page) ?? 1;
			if (targetPage === currentQueryPage) {
				if (targetPage !== this.meta.current_page) {
					this.fetchData(targetPage);
				}
				return;
			}
			this.updateRouteQuery({ page: targetPage === 1 ? null : targetPage });
		},

		openModal(item, options = {}) {
			this.clearShareFeedback();
			this.selectedItem = item;
			this.showModal = true;
			this.showBenefitForm = false;
			this.setBenefitInfo("");
			this.lastSharedItemId = this.parsePositiveInt(item?.id);
			if (!options.keepRoute) {
				const currentPage = this.parsePositiveInt(this.meta?.current_page);
				this.updateRouteQuery({
					ally_id: this.lastSharedItemId ?? null,
					page: currentPage && currentPage > 1 ? currentPage : null,
				});
				this.pendingSharedId = this.lastSharedItemId;
			}
			this.ensureAllyDetail(item);
			this.$nextTick(() => {
				new Swiper(".swiper-container", {
					loop: true,
					navigation: {
						nextEl: ".swiper-button-next",
						prevEl: ".swiper-button-prev",
					},
				});
			});
		},
		closeModal() {
			this.clearShareFeedback();
			this.showModal = false;
			this.showBenefitForm = false;
			this.selectedItem = null;
			this.setBenefitInfo("");
			if (this.$route.query.ally_id) {
				this.updateRouteQuery({ ally_id: null });
			}
			this.pendingSharedId = null;
			this.lastSharedItemId = null;
		},

		prev() {
			if (this.meta.current_page > 1) {
				this.changePage(this.meta.current_page - 1);
			}
		},

		next() {
			if (this.meta.current_page < this.meta.last_page) {
				this.changePage(this.meta.current_page + 1);
			}
		},

		goToPage(page) {
			this.changePage(page);
		},
	},
};
</script>

<style scoped>
.loader {
	border: 3px solid #f3f3f3;
	border-top: 3px solid #3498db;
	border-radius: 50%;
	width: 18px;
	height: 18px;
	animation: spin 1s linear infinite;
	display: inline-block;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}

	100% {
		transform: rotate(360deg);
	}
}

.apply {
	background-color: #ff6155;
	color: #fff;
	width: 100%;
	height: 40px;
	font-size: 16px;
	border-radius: 15px;
	cursor: pointer;
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 10px;
	position: relative;
}

.theme_common_box_two {
	width: 320px;
	height: max-content;
	border-radius: 15px;
	box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	background-color: white;
	font-family: Arial, sans-serif;
}

.theme_two_box_img {
	position: relative;
	overflow: hidden;
	border-top-left-radius: 15px;
	border-top-right-radius: 15px;
}

.discount-badge {
	position: absolute;
	top: 12px;
	left: 12px;
	padding: 6px 12px;
	border-radius: 20px;
	background-color: rgba(255, 97, 85, 0.9);
	color: #fff;
	font-weight: 600;
	font-size: 14px;
	z-index: 2;
}

.theme_two_box_img img {
	width: 100%;
	height: 220px;
	object-fit: cover;
	border-top-left-radius: 15px;
	border-top-right-radius: 15px;
	cursor: pointer;
}

.theme_two_box_content {
	padding: 6px 12px;
	text-align: left;
	margin-top: -5px;
}

.card-line {
	margin: 0;
	padding: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.card-line--name {
	font-size: 18px;
	font-weight: 700;
	color: #333;
}

.card-line--meta {
	font-size: 14px;
	font-weight: 500;
	color: #5d69d5;
	margin: 0;
}

.theme_two_box_content .score_rating .score_star_all i {
	color: #5d69d5;
	font-size: 14px;
}

.theme_two_box_content .review_rating .review_star_all i {
	color: #ffbc00;
	font-size: 14px;
}

.theme_two_box_content .text-blue-violet {
	font-size: 20px;
	font-weight: bold;
	color: #5d69d5;
	margin: 10px 0;
}

.theme_two_box_content .fs-4 {
	font-size: 14px;
	color: #333;
}

.theme_two_box_content .review_count {
	font-size: 14px;
	color: #333;
}

.review_star_all .fas.fa-star {
	color: #5d69d5;
	margin-left: 2px;
}

.review-star-color {
	color: #f1c40f;
}

.hotel-star-color {
	color: #5d69d5;
}

.v-select {
	height: auto;
}

.v-select .vs__dropdown-menu {
	max-height: 200px;
	overflow-y: auto;
}

.v-select .vs__selected-options {
	display: flex;
	flex-wrap: wrap;
	gap: 5px;
	max-height: 200px;
	overflow-y: auto;
}

.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.7);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
	backdrop-filter: blur(5px);
}

.modal-content {
	background: white;
	padding: 20px;
	border-radius: 15px;
	width: 90%;
	max-width: 600px;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
	position: relative;
	max-height: 80vh;
	overflow-y: auto;
	margin-top: 10vh;
}

.modal-header {
	display: flex;
	justify-content: flex-end;
}

.close-btn {
	background: none;
	border: none;
	font-size: 22px;
	cursor: pointer;
	color: #333;
}

.swiper-container {
	position: relative;
	width: 100%;
	border-top-left-radius: 10px;
	border-top-right-radius: 10px;
	overflow: hidden;
	background: #000;
	min-height: 35vh;
}

.swiper-wrapper {
	height: 35vh;
}

.swiper-slide {
 display: flex;
 align-items: center;
 justify-content: center;
 background: #000;
}

.swiper-modal-image {
 width: 100%;
 height: 100%;
 object-fit: contain;
 background: #000;
}

.swiper-button-next,
.swiper-button-prev {
	color: #fff;
	background: rgba(0, 0, 0, 0.45);
	padding: 10px;
	border-radius: 50%;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	top: 50% !important;
	opacity: 0;
	pointer-events: none;
	transition: opacity 0.25s ease;
}

.swiper-container:hover .swiper-button-next,
.swiper-container:hover .swiper-button-prev,
.swiper-button-next:focus,
.swiper-button-prev:focus {
	opacity: 1;
	pointer-events: auto;
}

@media (hover: none) and (pointer: coarse) {

	.swiper-button-next,
	.swiper-button-prev {
		opacity: 1;
		pointer-events: auto;
	}
}

.modal-info {
	padding: 20px;
}

.title-section {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;
}

.title-section h2 {
	font-size: 24px;
	margin: 0;
	color: #333;
}

.contact-icons {
	display: flex;
	gap: 10px;
	align-items: center;
}

.contact-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	border-radius: 50%;
	text-decoration: none;
	color: white;
	font-size: 16px;
	transition: background-color 0.3s ease;
	border: none;
	cursor: pointer;
}

.contact-icon.whatsapp {
	background-color: #25d366;
}

.contact-icon.website {
	background-color: #5d69d5;
}

.contact-icon.share {
	background-color: var(--coral-red-color);
}

.contact-icon:hover {
	opacity: 0.9;
}

.hover-text {
	position: absolute;
	right: 40px;
	white-space: nowrap;
	background-color: rgba(0, 0, 0, 0.8);
	color: white;
	padding: 5px 10px;
	border-radius: 5px;
	font-size: 14px;
	opacity: 1;
	transition: opacity 0.3s ease;
}

.share-feedback {
	margin-top: 8px;
	font-size: 12px;
	color: #555;
	text-align: right;
}

.modal-info p {
	font-size: 16px;
	margin-bottom: 10px;
	color: #555;
}

.discount-line {
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 10px 0;
	color: #333;
}

.discount-pill {
	display: inline-flex;
	align-items: center;
	padding: 4px 12px;
	border-radius: 999px;
	background-color: rgba(255, 97, 85, 0.12);
	color: #ff6155;
	font-weight: 600;
}

.benefit-feedback {
	margin-top: 8px;
	color: #b91c1c;
	font-size: 0.9rem;
	font-weight: 600;
}

.agreement-validity {
	color: #374151;
}

.benefit-actions {
	margin: 16px 0;
	display: flex;
	justify-content: flex-start;
}

.benefit-button {
	background: linear-gradient(135deg, #f97316, #ec4899);
	color: #ffffff;
	border: none;
	border-radius: 999px;
	font-weight: 600;
	padding: 10px 24px;
	cursor: pointer;
	transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.benefit-button:disabled {
	opacity: 0.55;
	cursor: not-allowed;
	box-shadow: none;
}

.benefit-button:not(:disabled):hover {
	transform: translateY(-2px);
	box-shadow: 0 12px 24px rgba(236, 72, 153, 0.3);
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.modal-info .category {
	font-weight: bold;
	color: #5d69d5;
}

.modal-info .location {
	color: #333;
}

.modal-info .investment {
	color: #333;
}

.modal-info .phone {
	color: #333;
}

.fab.fa-whatsapp,
.fas.fa-external-link-alt {
	color: white;
}

.fas.fa-map-marker-alt {
	font-size: 1.5em;
	color: #ff0000;
}

.multi-range {
	position: relative;
	height: 50px;
}

.position_ball {
	position: absolute;
	top: -23px;
	left: 0;
}

input[type="range"] {
	box-sizing: border-box;
	appearance: none;
	width: 100%;
	margin: 0;
	padding: 0 2px;
	overflow: hidden;
	border: 0;
	border-radius: 1px;
	outline: none;
	background: linear-gradient(grey, grey) no-repeat center;
	background-size: 100% 2px;
	pointer-events: none;
}

input[type="range"]:active,
input[type="range"]:focus {
	outline: none;
}

input[type="range"]::-webkit-slider-thumb {
	height: 28px;
	width: 28px;
	border-radius: 28px;
	background-color: #ff6155;
	position: relative;
	margin: 5px 0;
	cursor: pointer;
	appearance: none;
	pointer-events: all;
	box-shadow: 0 1px 4px 0.5px rgba(0, 0, 0, 0.25);
}

.multi-range input[type="range"]:nth-child(1)::-webkit-slider-thumb::before {
	background-color: red;
}

.multi-range input[type="range"]:nth-child(2) {
	background: none;
}

.multi-range input[type="range"]:nth-child(2)::-webkit-slider-thumb::before {
	background-color: grey;
}

.range-values {
	display: flex;
	justify-content: space-between;
	margin-top: 10px;
}
</style>
